import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { FormContent } from '@dreryk/react-components';

import { AdditionalInformationTextarea } from '../../shared/AdditionalInformationTextarea';
import { CitoCheckbox } from '../../shared/CitoCheckbox';
import { resolveDiagnosesFormPart } from '../../shared/common.utils';
import { DiagnosesWithBodySideInteractiveTable } from '../../shared/DiagnosesWithBodySideInteractiveTable';
import { ExaminationsPerformedTextarea } from '../../shared/ExaminationsPerformedTextarea';
import { IllnessesAlreadyPassedTextarea } from '../../shared/IllnessesAlreadyPassedTextarea';
import { ObjectiveTextarea } from '../../shared/ObjectiveTextarea';
import { StyledReferralForm } from '../../shared/StyledReferralForm';
import { UnitNameAndAddressTextarea } from '../../shared/UnitNameAndAddressTextarea';
import { referralTranslationKeys } from '../../translations';
import { Icd9ProcedureSelector } from '../../shared/Icd9ProcedureField/Icd9ProcedureSelector';
import { HealthcareFacilityLocationSpecialtyField } from '../../shared/HealthcareFacilitySpecialtyField/HealthcareFacilityLocationSpecialtyField';

import { ReferralToSpecialistFormBean, ReferralToSpecialistFormController } from './ReferralToSpecialistForm.types';

type ReferralToSpecialistFormChildrenProps = Pick<
   ReferralToSpecialistFormController,
   'handleSubmit' | 'handleSubmitDraft' | 'formState' | 'reset' | 'trigger'
>;
type ReferralToSpecialistFormProps = {
   readonly referralToSpecialistDraft: ReferralToSpecialistFormBean | null;
   readonly visitDiagnoses: VisitDiagnosis[] | undefined;
   readonly visitBeginDate: Date;
   readonly children: (childrenProps: ReferralToSpecialistFormChildrenProps) => JSX.Element;
};

const getInitialValues = (
   referralToSpecialistDraft: ReferralToSpecialistFormBean | null,
   visitDiagnoses: VisitDiagnosis[] | undefined
): ReferralToSpecialistFormBean => {
   return {
      healthcareFacilityLocationSpecialty: referralToSpecialistDraft?.healthcareFacilityLocationSpecialty ?? null,
      cito: referralToSpecialistDraft?.cito ?? false,
      illnessesAlreadyPassed: referralToSpecialistDraft?.illnessesAlreadyPassed ?? null,
      objective: referralToSpecialistDraft?.objective ?? null,
      examinationsAlreadyPerformed: referralToSpecialistDraft?.examinationsAlreadyPerformed ?? null,
      additionalInformation: referralToSpecialistDraft?.additionalInformation ?? null,
      unitNameAndAddress: referralToSpecialistDraft?.unitNameAndAddress ?? null,
      diagnoses: resolveDiagnosesFormPart(referralToSpecialistDraft?.diagnoses, visitDiagnoses),
      icd9Procedures: referralToSpecialistDraft?.icd9Procedures ?? [],
   };
};

export const ReferralToSpecialistForm = (props: ReferralToSpecialistFormProps): JSX.Element => {
   const {
      register,
      formState,
      handleSubmit,
      handleSubmitDraft,
      reset,
      trigger,
      control,
   } = useFormController<ReferralToSpecialistFormBean>(
      getInitialValues(props.referralToSpecialistDraft, props.visitDiagnoses)
   );

   return (
      <StyledReferralForm onSubmit={(event) => event.preventDefault()} noValidate>
         <FormContent testId={'referralToSpecialist'}>
            <CitoCheckbox register={register} />
            <HealthcareFacilityLocationSpecialtyField specialtyType={'medicalClinic'} register={register} />
            <UnitNameAndAddressTextarea register={register} />
            <DiagnosesWithBodySideInteractiveTable
               register={register}
               visitDiagnoses={props.visitDiagnoses}
               dictionaryValidOn={props.visitBeginDate}
               required
            />
            <Icd9ProcedureSelector
               control={control}
               dictionaryValidOn={props.visitBeginDate}
               defaultValues={props.referralToSpecialistDraft?.icd9Procedures}
            />
            <ObjectiveTextarea
               register={register}
               label={referralTranslationKeys.referralToSpecialist.referralObjective}
               placeholder={referralTranslationKeys.referralToSpecialist.insertReferralObjective}
               required
            />
            <IllnessesAlreadyPassedTextarea register={register} />
            <ExaminationsPerformedTextarea register={register} />
            <AdditionalInformationTextarea register={register} />
            {props.children({
               handleSubmit: handleSubmit,
               handleSubmitDraft: handleSubmitDraft,
               formState: formState,
               reset: reset,
               trigger: trigger,
            })}
         </FormContent>
      </StyledReferralForm>
   );
};
